import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { config }     from '@/lib/config'
import { PATHS }      from '@/lib/routes'
import { Typography } from '@/components/ui/Typography'
import { useMobileSidebar } from '@/providers/MobileSidebarProvider'
import { useTheme }   from '@/providers/ThemeProvider'
import { getBrandLogos } from '@/theme/brandAssets'
import { useCanTransfer } from '@/lib/useCanTransfer'
import { useSidebar } from './useSidebar'
import type { NavItem } from './Sidebar.types'

// Brand-aware logos — same resolver as LoginView. `needsContrastBadge` brands
// (e.g. MasPay's dark-green icon) get wrapped in a white pill so they stay
// legible on a dark sidebar surface.
const { dark: logoDark, light: logoLight } = getBrandLogos()

// ---------------------------------------------------------------------------
// Icon map — one icon per route
// ---------------------------------------------------------------------------

const NAV_ICONS: Record<string, ReactNode> = {
  [PATHS.dashboard]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z" />
      <path d="M9 21V12h6v9" />
    </svg>
  ),
  [PATHS.transactions]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
      <path d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01" strokeLinecap="round" />
    </svg>
  ),
  [PATHS.sendMoney]: (
    // Paper-plane — mismo icono del boton "Enviar" del dashboard para
    // mantener el lenguaje visual consistente entre los 3 puntos de entry
    // a /send-money (sidebar, bottom nav, balance card).
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  [PATHS.balances]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" strokeLinecap="round" />
    </svg>
  ),
  [PATHS.notifications]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" />
      <path d="M13.73 21a2 2 0 01-3.46 0" strokeLinecap="round" />
    </svg>
  ),
  [PATHS.profile]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
    </svg>
  ),
  [PATHS.settings]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" strokeLinecap="round" />
    </svg>
  ),
  [PATHS.profiles]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 3.13a4 4 0 010 7.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  [PATHS.moreOptions]: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </svg>
  ),
}

// ---------------------------------------------------------------------------
// Inner content — reused by desktop aside + mobile drawer
// ---------------------------------------------------------------------------

function SidebarContent({
  items,
  isActive,
  handleLogout,
  logoutLabel,
  logoSrc,
}: {
  items:        NavItem[]
  isActive:     (href: string) => boolean
  handleLogout: () => void
  logoutLabel:  string
  logoSrc:      string
}) {
  const { canTransfer, notifyNoPermission } = useCanTransfer()
  return (
    <>
      {/* Logo — clickable, navigates to /dashboard. Fila más alta para que el
          logo ocupe más presencia en el sidebar. */}
      <Link
        to={PATHS.dashboard}
        className="h-20 flex items-center justify-center px-5 border-b border-[var(--color-border)] hover:bg-[var(--color-surface-control)] transition-colors"
      >
        <img
          src={logoSrc}
          alt={config.companyLabel}
          className="h-24 w-auto py-2"
          style={{ filter: 'drop-shadow(white 0px 0px 1px) drop-shadow(white 0px 0px 1px)' }}
        />
      </Link>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map(({ href, label, icon }) => {
          const active = isActive(href)

          // Transferir bloqueado si el balance activo no permite transferir:
          // item inactivo (opacidad + cursor not-allowed) y toast al click.
          if (href === PATHS.sendMoney && !canTransfer) {
            return (
              <button
                key={href}
                type="button"
                onClick={notifyNoPermission}
                aria-disabled="true"
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-list text-[var(--color-text-muted)] opacity-50 cursor-not-allowed"
              >
                {icon}
                <Typography variant="body-semibold" color="inherit">{label}</Typography>
              </button>
            )
          }

          // El item ACTIVO siempre se muestra como pill brand-gradient — mismo
          // tratamiento visual que el CTA Enviar/Transferir, sin importar de
          // que ruta se trate. Asi el usuario tiene un solo lenguaje visual
          // de "estoy aqui".
          return (
            <Link
              key={href}
              to={href}
              className={[
                'flex items-center gap-3 px-3 py-2.5 transition-all duration-150',
                active
                  ? 'rounded-full bg-gradient-primary text-white shadow-brand hover:opacity-90 active:scale-[0.98]'
                  : 'rounded-list text-[var(--color-text-muted)] hover:bg-[var(--color-surface-control)] hover:text-[var(--color-text-primary)]',
              ].join(' ')}
            >
              {icon}
              <Typography variant="body-semibold" color="inherit">{label}</Typography>
            </Link>
          )
        })}
      </nav>

      {/* Logout */}
      <div className="px-3 py-4 border-t border-[var(--color-border)]">
        <button type="button"
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-list text-[var(--color-text-muted)] hover:bg-[var(--color-surface-control)] hover:text-[var(--color-danger)] transition-all duration-150"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="16 17 21 12 16 7" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="21" y1="12" x2="9" y2="12" strokeLinecap="round" />
          </svg>
          <Typography variant="body-semibold" color="inherit">{logoutLabel}</Typography>
        </button>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Component — renders desktop aside + mobile drawer overlay
// ---------------------------------------------------------------------------

export function Sidebar() {
  const { navItems, isActive, handleLogout, logoutLabel } = useSidebar()
  const { open: mobileOpen, close } = useMobileSidebar()
  const { theme } = useTheme()

  const items: NavItem[] = navItems.map(item => ({
    ...item,
    icon: NAV_ICONS[item.href],
  }))

  // billeteraPais: logoLight is the coloured wordmark (good on light surface),
  // logoDark the white version (good on dark surface).
  const logoSrc = theme === 'dark' ? logoDark : logoLight

  const contentProps = { items, isActive, handleLogout, logoutLabel, logoSrc }

  return (
    <>
      {/* Desktop sidebar — always rendered, hidden below lg */}
      <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-[var(--color-surface)] border-r border-[var(--color-border)] shrink-0">
        <SidebarContent {...contentProps} />
      </aside>

      {/* Mobile drawer — fixed overlay, controlled by MobileSidebarProvider */}
      <div
        className={[
          'lg:hidden fixed inset-0 z-40 transition-opacity duration-200',
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        ].join(' ')}
        aria-hidden={!mobileOpen}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={close} />

        {/* Panel */}
        <aside
          className={[
            'relative h-full w-72 max-w-[85vw] flex flex-col bg-[var(--color-surface)] border-r border-[var(--color-border)] shadow-modal transition-transform duration-200',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          ].join(' ')}
        >
          <SidebarContent {...contentProps} />
        </aside>
      </div>
    </>
  )
}
